import React, { useState } from 'react';
import ImageLightbox from './MessagesImageLightbox';
import './MessageAttachments.scss';
interface Attachment {
  id: string;
  type: 'image' | 'video' | 'pdf' | 'file';
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storage: {
    url: string;
    blobPath?: string;
    thumbnailUrl?: string;
  };
  imageMeta?: {
    width: number;
    height: number;
  };
  videoMeta?: {
    durationSec: number;
    width: number;
    height: number;
  };
}

interface MessageAttachmentsProps {
  attachments: Attachment[];
  sender: 'self' | 'other';
}

const MessageAttachments: React.FC<MessageAttachmentsProps> = ({ attachments, sender }) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageClick = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getBlobPath = (attachment: Attachment): string => {
    if (attachment.storage.blobPath) return attachment.storage.blobPath;
    try {
      const u = new URL(attachment.storage.url);
      // pathname looks like /dm-attachments/conversations/.../file.ext
      const path = u.pathname.replace(/^\//, '');
      const parts = path.split('/');
      // remove container segment (dm-attachments)
      if (parts.length > 1) return parts.slice(1).join('/');
      return path;
    } catch {
      return '';
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const blobPath = getBlobPath(attachment);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/attachments/download-url`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          blobPath,
          fileName: attachment.fileName,
          mimeType: attachment.mimeType
        })
      });
      if (!res.ok) throw new Error('Failed to get download URL');
      const { url } = await res.json();
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download failed', e);
      // graceful fallback: open in new tab
      window.open(attachment.storage.url, '_blank', 'noopener');
    }
  };

  // Group attachments by type for better layout
  const imageAttachments = attachments.filter(a => a.type === 'image');
  const videoAttachments = attachments.filter(a => a.type === 'video');
  const fileAttachments = attachments.filter(a => a.type === 'pdf' || a.type === 'file');

  // Determine grid layout for images
  const getImageGridClass = (count: number) => {
    if (count === 1) return 'message-attachments__grid--single';
    if (count === 2) return 'message-attachments__grid--double';
    if (count === 3) return 'message-attachments__grid--triple';
    return 'message-attachments__grid--multiple';
  };

  // Ensure we have attachments to display
  if (!attachments || attachments.length === 0) {
    console.warn('MessageAttachments called with no attachments');
    return null;
  }

  return (
    <div className={`message-attachments message-attachments--${sender}`}>
      {imageAttachments.length > 0 && (
        <div className={`message-attachments__grid ${getImageGridClass(imageAttachments.length)}`}>
          {imageAttachments.map((attachment, index) => (
            <div 
              key={attachment.id} 
              className="message-attachments__image-container"
              onClick={() => handleImageClick(index)}
            >
              <img
                src={attachment.storage.thumbnailUrl || attachment.storage.url}
                alt={attachment.fileName}
                className="message-attachments__image"
                loading="lazy"
              />
              <div className="message-attachments__image-overlay">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                  <path d="M11 8v6M8 11h6" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}

      {videoAttachments.map(attachment => (
        <div key={attachment.id} className="message-attachments__video-container">
          <video
            controls
            className="message-attachments__video"
            preload="metadata"
          >
            <source src={attachment.storage.url} type={attachment.mimeType} />
            Your browser does not support the video tag.
          </video>
          {attachment.videoMeta && (
            <div className="message-attachments__video-duration">
              {formatDuration(attachment.videoMeta.durationSec)}
            </div>
          )}
        </div>
      ))}

      {fileAttachments.map(attachment => (
        <div key={attachment.id} className="message-attachments__file">
          <div className="message-attachments__file-icon">
            {attachment.type === 'pdf' ? '📄' : '📎'}
          </div>
          <div className="message-attachments__file-info">
            <div className="message-attachments__file-name">
              {attachment.fileName}
            </div>
            <div className="message-attachments__file-size">
              {formatFileSize(attachment.sizeBytes)}
            </div>
          </div>
          <button
            className="message-attachments__file-download"
            onClick={() => handleDownload(attachment)}
            aria-label="Download file"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </button>
        </div>
      ))}

      {lightboxOpen && imageAttachments.length > 0 && (
        <ImageLightbox
          images={imageAttachments}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default MessageAttachments;


